/**
 * Polyfill `requestIdleCallback` / `cancelIdleCallback` dla Safari/WebKit.
 *
 * WebKit historycznie NIE implementuje tych API (status "non-standard / not
 * supported", aktualne nawet w Safari 26). Kod aplikacji (`raporty-dashboard`)
 * oraz biblioteki (CodeMirror, Radix password-toggle) wolaja je bez fallbacku,
 * co na Safari rzuca `ReferenceError: Can't find variable: requestIdleCallback`
 * i wywala caly render (issue #234). Globalny shim na `setTimeout` zamyka temat
 * raz, dla wszystkich call-site — appki i zaleznosci.
 *
 * Shim odwzorowuje kontrakt: callback dostaje `IdleDeadline` z `timeRemaining()`
 * (budzet ~50ms jak w spec) i `didTimeout`. Idempotentny — nie nadpisuje
 * natywnej implementacji (Chrome/Firefox), wiec na nich zostaje real scheduling.
 */

type IdleTarget = {
	requestIdleCallback?: typeof requestIdleCallback;
	cancelIdleCallback?: typeof cancelIdleCallback;
	setTimeout: typeof setTimeout;
	clearTimeout: typeof clearTimeout;
};

export function installRequestIdleCallbackPolyfill(
	target: IdleTarget = globalThis as unknown as IdleTarget,
): void {
	if (typeof target.requestIdleCallback === "function") {
		return;
	}

	target.requestIdleCallback = ((
		callback: IdleRequestCallback,
		options?: IdleRequestOptions,
	): number => {
		const start = Date.now();
		return target.setTimeout(() => {
			callback({
				didTimeout: false,
				timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
			});
		}, options?.timeout ?? 1) as unknown as number;
	}) as typeof requestIdleCallback;

	target.cancelIdleCallback = ((id: number): void => {
		target.clearTimeout(id as unknown as ReturnType<typeof setTimeout>);
	}) as typeof cancelIdleCallback;
}

// Self-install przy imporcie — `main.tsx` importuje ten modul jako PIERWSZY,
// dzieki czemu shim istnieje zanim wykona sie side-effect jakiegokolwiek innego
// importu (kolejnosc importow = kolejnosc ewaluacji modulow w ES).
installRequestIdleCallbackPolyfill();
